import { c as _c } from "react/compiler-runtime";
import { Button, DropdownMenu, Input, InputDecorator, Skeleton } from '@common/components';
import SvgRefresh from '@common/icon/SvgRefresh';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { UnauthQueryOptions } from '@/api';
import { resolveDefaultDonationPurpose } from '@/app/donation/_utils/giftOrder';
import { parseAmountInput } from '@/app/donation/_utils/parseAmountInput';
import { DonationTermsList, useDonationTerms } from '@/components/organism/DonationTerms';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSubscribedState } from '@/hooks/useSubscribedState';
import { DEFAULT_DONATION_AMOUNT, MIN_DONATION_AMOUNT } from './constants';
interface PrefillFormProps {
  institutionId: number;
  defaultDonationBusinessId?: number;
  hideAgreements?: boolean;
  onSubmit: (data: {
    amount: number;
    purpose: string;
  }) => void;
}
export const PrefillForm = t0 => {
  const $ = _c(64);
  const {
    institutionId,
    defaultDonationBusinessId,
    hideAgreements: t1,
    onSubmit
  } = t0;
  const hideAgreements = t1 === undefined ? false : t1;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const terms = useDonationTerms();
  const [amount, setAmount] = useState(DEFAULT_DONATION_AMOUNT);
  let t2;
  if ($[0] !== institutionId) {
    t2 = UnauthQueryOptions["get:/wello/v2/allows/home/donation-business/institution/{institutionId}"]({
      path: {
        institutionId
      }
    });
    $[0] = institutionId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = {
      ...t2,
      select: _temp
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: donationBusinessPurposes
  } = useQuery(t3);
  let t4;
  if ($[4] !== donationBusinessPurposes) {
    t4 = donationBusinessPurposes ?? [];
    $[4] = donationBusinessPurposes;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== defaultDonationBusinessId || $[7] !== t4) {
    t5 = resolveDefaultDonationPurpose(t4, defaultDonationBusinessId);
    $[6] = defaultDonationBusinessId;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const defaultPurpose = t5;
  const [purpose, setPurpose] = useSubscribedState(defaultPurpose);
  const [amountDraft, setAmountDraft] = useState(null);
  let t6;
  if ($[9] !== amount || $[10] !== amountDraft) {
    t6 = amountDraft !== null && parseAmountInput(amountDraft) === amount ? amountDraft : amount.toLocaleString();
    $[9] = amount;
    $[10] = amountDraft;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const amountInputValue = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = value => {
      const nextAmount = parseAmountInput(value);
      if (nextAmount === null) {
        return;
      }
      setAmountDraft(value.includes(".") ? value : null);
      setAmount(nextAmount);
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleAmountChange = t7;
  let t8;
  if ($[13] !== defaultPurpose || $[14] !== setPurpose || $[15] !== terms) {
    t8 = () => {
      setAmount(DEFAULT_DONATION_AMOUNT);
      setPurpose(defaultPurpose);
      terms.reset();
    };
    $[13] = defaultPurpose;
    $[14] = setPurpose;
    $[15] = terms;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleReset = t8;
  const t9 = isDesktopViewport ? "H56" : "H48";
  let t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e => handleAmountChange(e.target.value);
    t11 = <span className="font-400 text-Gray-900 typo-body3">원</span>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  let t12;
  if ($[19] !== amountInputValue || $[20] !== t9) {
    t12 = <Input dimensions={t9} placeholder="0" inputMode="numeric" value={amountInputValue} onChange={t10} rightContent={t11} maxLength={16} className="mb-24" />;
    $[19] = amountInputValue;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== donationBusinessPurposes || $[23] !== isDesktopViewport || $[24] !== purpose || $[25] !== setPurpose) {
    t13 = donationBusinessPurposes?.length === 1 ? null : <InputDecorator label={"\uAE30\uBD80\uC6A9\uB3C4 \uC120\uD0DD"}>{donationBusinessPurposes ? <DropdownMenu dimensions={isDesktopViewport ? "H56" : "H48"} options={donationBusinessPurposes.map(_temp2)} value={purpose} onChange={setPurpose} placeholder={"\uAE30\uBD80\uC6A9\uB3C4\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694"} className="mb-24 w-full" /> : <Skeleton className="mb-24 h-48 w-full rounded-small" />}</InputDecorator>;
    $[22] = donationBusinessPurposes;
    $[23] = isDesktopViewport;
    $[24] = purpose;
    $[25] = setPurpose;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className={cn("flex gap-8 font-500 text-Gray-800 typo-body3", "mobile-tablet:typo-body4")}>기부 금액</span>;
    t15 = cn("font-700 typo-h6", "mobile-tablet:typo-body1");
    $[27] = t14;
    $[28] = t15;
  } else {
    t14 = $[27];
    t15 = $[28];
  }
  let t16;
  if ($[29] !== amount) {
    t16 = amount.toLocaleString("ko-KR");
    $[29] = amount;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = <div className="mb-20 flex items-center justify-between border-y-1 border-Gray-300 py-20">{t14}<span className={t15}>{t16}원</span></div>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== amount) {
    t18 = amount < MIN_DONATION_AMOUNT && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }} exit={{
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }} className="flex flex-col gap-8"><p className="text-center font-400 text-R-500 typo-body4">{MIN_DONATION_AMOUNT.toLocaleString()}원 이상부터 기부 가능해요.</p></motion.div>;
    $[33] = amount;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = <AnimatePresence>{t18}</AnimatePresence>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = hideAgreements && "hidden";
  let t21;
  if ($[37] !== t20) {
    t21 = cn("mb-32", t20);
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t21 || $[40] !== terms) {
    t22 = <DonationTermsList {...terms} className={t21} />;
    $[39] = t21;
    $[40] = terms;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t17 || $[43] !== t19 || $[44] !== t22) {
    t23 = <div className="flex flex-col">{t17}{t19}{t22}</div>;
    $[42] = t17;
    $[43] = t19;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SvgRefresh className="size-16" />;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== handleReset) {
    t25 = <Button theme="sub-line" onClick={handleReset}>{t24}초기화</Button>;
    $[47] = handleReset;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const t26 = !hideAgreements && !terms.isAllRequiredAgreed || !amount || !purpose || amount < MIN_DONATION_AMOUNT;
  let t27;
  if ($[49] !== amount || $[50] !== onSubmit || $[51] !== purpose) {
    t27 = () => onSubmit({
      amount,
      purpose
    });
    $[49] = amount;
    $[50] = onSubmit;
    $[51] = purpose;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t26 || $[54] !== t27) {
    t28 = <Button disabled={t26} onClick={t27} className="grow">기부하기</Button>;
    $[53] = t26;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t25 || $[57] !== t28) {
    t29 = <footer className="flex gap-8">{t25}{t28}</footer>;
    $[56] = t25;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t12 || $[60] !== t13 || $[61] !== t23 || $[62] !== t29) {
    t30 = <div>{t12}{t13}{t23}{t29}</div>;
    $[59] = t12;
    $[60] = t13;
    $[61] = t23;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  return t30;
};
function _temp(t0) {
  const {
    context
  } = t0;
  return context?.contents;
}
function _temp2(t0) {
  const {
    donationBusinessName: donation_business_name
  } = t0;
  return {
    label: donation_business_name,
    value: donation_business_name
  };
}